import { normalizeInvoiceAppointmentTimes } from "./invoiceAppointmentTimes";

const decimalFromMinorUnits = (money) => {
  const amount = money && Number(money.amount_minor);
  return Number.isFinite(amount) ? amount / 100 : undefined;
};

const decimalValue = (value, fallback) => {
  if (value !== undefined && value !== null && value !== "") {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }
  return fallback;
};

const moneyValue = (money, fallback = 0) => decimalFromMinorUnits(money) ?? decimalValue(money, fallback);

export const formatClientInvoiceAmount = (value) => {
  const amount = moneyValue(value, 0);
  return amount.toFixed(2);
};

export const formatClientInvoiceDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Australia/Brisbane",
  }).format(date);
};

const activeFlag = (value) => {
  if (value === undefined || value === null || value === "") return 1;
  return value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true" ? 1 : 0;
};

const paymentType = (payment) => {
  if (payment.type) return payment.type;
  const paidBy = String(payment.paid_by || payment.paidby || "").toLowerCase();
  if (paidBy.includes("depositrefund") || paidBy.includes("refund")) return "DepositRefund";
  if (paidBy.includes("deposit")) return "Deposit";
  return "Sale";
};

const normalizePayment = (payment) => {
  if (!payment || typeof payment !== "object") return payment;
  return {
    ...payment,
    ...(payment.amount && typeof payment.amount === "object" && Object.prototype.hasOwnProperty.call(payment.amount, "currency") ? {currency: payment.amount.currency} : {}),
    amount: payment.amount && typeof payment.amount === "object"
      ? moneyValue(payment.amount)
      : decimalValue(payment.amount, 0),
    created_at: payment.created_at ?? payment.payment_date,
    paidby: payment.paidby ?? payment.paid_by ?? "",
    type: paymentType(payment),
  };
};

const normalizeCartLine = (line) => {
  if (!line || typeof line !== "object") return line;
  const rawType = line.type ?? line.item?.type ?? "";
  const type = String(rawType).toLowerCase() === "subscription" ? "Subscription" : rawType;
  const cost = line.cost !== undefined
    ? decimalValue(line.cost, 0)
    : moneyValue(line.line_total ?? line.unit_amount, 0);
  const normalized = {
    ...line,
    cost,
    qty: line.qty ?? line.quantity ?? 1,
    type,
  };

  if (["Appointment", "AddOnServices", "AddAnotherServices"].includes(type)) {
    normalized.services = line.services || line.appointment?.service || { name: line.name || "" };
    normalized.staff = line.staff || line.appointment?.staff || {};
  }
  if (type === "Product" && !normalized.products) normalized.products = { id: line.item?.id, name: line.name };
  if (type === "Service" && !normalized.services) normalized.services = { id: line.item?.id, name: line.name };
  if (type === "Subscription" && !normalized.subscription) normalized.subscription = { id: line.item?.id, name: line.name };
  if (type === "Membership" && !normalized.membership) normalized.membership = { id: line.item?.id, name: line.name };
  return normalized;
};

const normalizeAppointment = (appointment) => {
  if (!appointment || typeof appointment !== "object") return appointment;
  const start = appointment.start_datetime;
  const end = appointment.end_datetime;
  return {
    ...appointment,
    addonservices: (appointment.addonservices ?? []).map(normalizeAppointment),
    addanotherservices: (appointment.addanotherservices ?? []).map(normalizeAppointment),
    dateof: appointment.dateof ?? (typeof start === "string" ? start.slice(0, 10) : ""),
    end_time: appointment.end_time ?? (typeof end === "string" ? end.slice(11, 19) : ""),
    payment: Array.isArray(appointment.payment) ? appointment.payment.map(normalizePayment) : [],
    service: appointment.service ?? (appointment.service_id ? { id: appointment.service_id, name: "" } : undefined),
    start_time: appointment.start_time ?? (typeof start === "string" ? start.slice(11, 19) : ""),
    staff: appointment.staff ?? {},
  };
};

export const normalizeClientInvoiceRow = (row, values = {}) => {
  if (!row || typeof row !== "object") return row;
  row = normalizeInvoiceAppointmentTimes(row);

  const total = row.totalprice === undefined ? moneyValue(row.total, 0) : decimalValue(row.totalprice, 0);
  const explicitCartTotal = row.cart_sum_cost === undefined
    ? moneyValue(row.cart_total, undefined)
    : decimalValue(row.cart_sum_cost, undefined);
  const cartTotal = explicitCartTotal === undefined || (explicitCartTotal === 0 && total > 0)
    ? total
    : explicitCartTotal;
  const paid = row.total_pay === undefined ? moneyValue(row.paid, 0) : decimalValue(row.total_pay, 0);
  const clientId = row.client_id ?? row.client?.id ?? values.client_id;
  const payments = Array.isArray(row.payment)
    ? row.payment.map(normalizePayment)
    : (Array.isArray(row.payments) ? row.payments.map(normalizePayment) : []);

  return {
    ...row,
    ...(clientId === undefined ? {} : { client_id: clientId }),
    appointments: Array.isArray(row.appointments) ? row.appointments.map(normalizeAppointment) : [],
    cart: Array.isArray(row.cart) ? row.cart.map(normalizeCartLine) : [],
    cart_sum_cost: cartTotal,
    invoicedate: row.invoicedate ?? row.invoice_date ?? row.created_at ?? null,
    invoice_number: row.invoice_number ?? null,
    is_active: activeFlag(row.is_active),
    is_deposit_refund: row.is_deposit_refund ?? 0,
    payment: payments,
    total_pay: paid,
    totaldeposit: row.totaldeposit ?? paid,
    totalprice: total,
  };
};

export const normalizeClientInvoiceResponse = (response, values = {}) => {
  if (Array.isArray(response)) return response.map((row) => normalizeClientInvoiceRow(row, values));
  if (!response || typeof response !== "object") return response;
  if (!Array.isArray(response.data)) return normalizeClientInvoiceRow(response, values);

  const currentPage = Number(response.current_page);
  const lastPage = Number(response.last_page);
  const nextPage = response.next_page_url ?? (
    Number.isInteger(currentPage) && Number.isInteger(lastPage) && currentPage < lastPage
      ? `afterlogin/client/clientinvoice?page=${currentPage + 1}`
      : null
  );

  return {
    ...response,
    data: response.data.map((row) => normalizeClientInvoiceRow(row, values)),
    next_page_url: nextPage,
  };
};
